import React, { useEffect, useRef, useState, useCallback } from "react";
import {
  HMSRoomProvider,
  useHMSActions,
  useHMSStore,
  selectPeers,
  selectIsConnectedToRoom,
  selectHMSMessages,
  selectLocalPeer,
  selectIsLocalAudioEnabled,
  selectIsLocalVideoEnabled,
  selectIsLocalScreenShared,
  selectPeerScreenSharing,
  selectScreenShareByPeerID,
} from "@100mslive/react-sdk";
import { useParams, useNavigate } from "react-router-dom";
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  Hand,
  PhoneOff,
  Send,
  MessageSquare,
  Loader2,
  ScreenShare,
  X,
  Smile,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import axiosInstance from "../../api/axiosInstance";

/* ─── Reactions ─────────────────────────────────────────────── */
const REACTIONS = ["👍", "❤️", "😂", "🎉", "👏", "🔥", "😮", "💯"];

/* ─── Floating Reaction ─────────────────────────────────────── */
const FloatingReaction = ({ emoji, id, onDone }) => {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  const left = 10 + Math.random() * 80;

  return (
    <motion.div
      className="fixed bottom-28 z-50 text-4xl pointer-events-none select-none"
      style={{ left: `${left}%` }}
      initial={{ opacity: 1, y: 0 }}
      animate={{ opacity: 0, y: -220 }}
      transition={{ duration: 3, ease: "easeOut" }}
    >
      {emoji}
    </motion.div>
  );
};

/* ─── Control Button ─────────────────────────────────────────── */
const ControlBtn = ({ onClick, icon, label, danger = false, highlight = false }) => (
  <button onClick={onClick} className="flex flex-col items-center gap-1 group">
    <div className={`w-12 h-12 rounded-full flex items-center justify-center transition-all active:scale-95
      ${danger ? "bg-red-600 hover:bg-red-700" : highlight ? "bg-blue-600 hover:bg-blue-700" : "bg-slate-800 hover:bg-slate-700"}
    `}>
      <span className="text-white">{icon}</span>
    </div>
    <span className="text-[10px] text-slate-400 group-hover:text-slate-200 truncate max-w-[60px]">{label}</span>
  </button>
);

/* ─── Video Tile ─────────────────────────────────────────────── */
function VideoTile({ peer }) {
  const videoRef = useRef(null);
  const screenRef = useRef(null);
  const hmsActions = useHMSActions();

  // Reactively track the screen share track for this peer ID
  const screenTrack = useHMSStore(selectScreenShareByPeerID(peer.id));

  let peerMeta = {};
  try { peerMeta = peer.metadata ? JSON.parse(peer.metadata) : {}; } catch {}
  const hasRaisedHand = !peer.isLocal && peerMeta.isHandRaised;

  useEffect(() => {
    const el = videoRef.current;
    if (el && peer.videoTrack) {
      hmsActions.attachVideo(peer.videoTrack, el);
    }
    return () => {
      if (el && peer.videoTrack) hmsActions.detachVideo(peer.videoTrack, el);
    };
  }, [peer.videoTrack, hmsActions]);

  useEffect(() => {
    const el = screenRef.current;
    if (el && screenTrack) {
      hmsActions.attachVideo(screenTrack.id, el);
    }
    return () => {
      if (el && screenTrack) hmsActions.detachVideo(screenTrack.id, el);
    };
  }, [screenTrack, hmsActions]);

  const isScreenSharing = !!screenTrack;

  return (
    <div className="relative bg-slate-900 rounded-2xl overflow-hidden border border-slate-800 flex-1 min-h-0">
      {isScreenSharing ? (
        <video ref={screenRef} autoPlay playsInline className="w-full h-full object-contain" />
      ) : (
        <video ref={videoRef} autoPlay muted={peer.isLocal} playsInline className="w-full h-full object-cover" />
      )}
      <div className="absolute bottom-3 left-3 flex items-center gap-2">
        <span className="bg-black/60 backdrop-blur-sm text-white text-xs font-bold px-2.5 py-1 rounded-full">
          {peer.name} {peer.isLocal && "(You)"}
        </span>
        {hasRaisedHand && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-lg"
          >
            ✋
          </motion.span>
        )}
      </div>
    </div>
  );
}

/* ─── Main User Room ──────────────────────────────────────────── */
function UserRoom() {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  const hmsActions = useHMSActions();

  const peers = useHMSStore(selectPeers);
  const localPeer = useHMSStore(selectLocalPeer);
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const messages = useHMSStore(selectHMSMessages);

  const isAudioOn = useHMSStore(selectIsLocalAudioEnabled);
  const isVideoOn = useHMSStore(selectIsLocalVideoEnabled);
  const isScreenShared = useHMSStore(selectIsLocalScreenShared);

  const [chat, setChat] = useState("");
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [lastReadCount, setLastReadCount] = useState(0);
  const [showReactions, setShowReactions] = useState(false);
  const [floatingReactions, setFloatingReactions] = useState([]);
  const chatEndRef = useRef(null);
  const prevMsgLen = useRef(0);

  /* Auto-scroll */
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (isChatOpen) setLastReadCount(chatMessages.length);// eslint-disable-next-line
  }, [isChatOpen, messages]);

  /* Join on mount */
  useEffect(() => {
    let isMounted = true;
    const join = async () => {
      try {
        const res = await axiosInstance.get(`/users/bookings/${bookingId}/video-token`);
        if (isMounted) {
          await hmsActions.join({
            authToken: res.data.token,
            userName: `User_${Math.floor(Math.random() * 1000)}`,
            rememberDeviceSelection: true,
            settings: { isAudioMuted: false, isVideoMuted: false },
          });
        }
      } catch (err) {
        console.error("Join error:", err);
      }
    };
    join();
    return () => {
      isMounted = false;
      hmsActions.leave();
      axiosInstance.post(`/users/bookings/${bookingId}/video-leave`).catch(() => {});
    };
  }, [bookingId, hmsActions]);

  /* Incoming reactions from remote peers */
  useEffect(() => {
    const newMsgs = messages.slice(prevMsgLen.current);
    newMsgs.forEach((m) => {
      if (m.message.startsWith("REACTION:") && m.senderName !== localPeer?.name) {
        const emoji = m.message.replace("REACTION:", "");
        const id = Date.now() + Math.random();
        setFloatingReactions((prev) => [...prev, { emoji, id }]);
      }
    });
    prevMsgLen.current = messages.length;// eslint-disable-next-line
  }, [messages]);

  const removeFloating = useCallback((id) => {
    setFloatingReactions((prev) => prev.filter((r) => r.id !== id));
  }, []);

  /* Handlers */
  const toggleAudio = () => hmsActions.setLocalAudioEnabled(!isAudioOn);
  const toggleVideo = () => hmsActions.setLocalVideoEnabled(!isVideoOn);

  const toggleScreenShare = async () => {
    try {
      await hmsActions.setScreenShareEnabled(!isScreenShared);
    } catch (error) {
      console.error("Screen share error:", error);
      alert(error.message || "Screen share failed");
    }
  };

  const leaveCall = async () => {
    await hmsActions.leave();
    navigate("/profile/my-bookings");
  };

  const peerSharing = useHMSStore(selectPeerScreenSharing);
  const anyoneSharing = peerSharing || isScreenShared;

  const sendMessage = () => {
    if (!chat.trim()) return;
    hmsActions.sendBroadcastMessage(chat);
    setChat("");
  };

  const sendReaction = async (emoji) => {
    const id = Date.now() + Math.random();
    setFloatingReactions((prev) => [...prev, { emoji, id }]);
    await hmsActions.sendBroadcastMessage(`REACTION:${emoji}`);
    setShowReactions(false);
  };

  const isHandRaised = (() => {
    try { return localPeer?.metadata ? JSON.parse(localPeer.metadata).isHandRaised : false; } catch { return false; }
  })();

  const toggleRaiseHand = async () => {
    await hmsActions.changeMetadata(JSON.stringify({ isHandRaised: !isHandRaised }));
  };

  /* Derived */
  const uniquePeers = peers.filter(
    (peer, index, self) => index === self.findIndex((p) => p.id === peer.id)
  );

  const chatMessages = messages.filter((m) => !m.message.startsWith("REACTION:"));
  const unread = chatMessages.length - lastReadCount;

  /* Loading state */
  if (!isConnected) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-slate-900 z-[999]">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-white text-xl font-bold">Connecting to session...</p>
          <p className="text-slate-400 text-sm mt-2">Please wait while we set up your call</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950 flex flex-col overflow-hidden">

      {/* ── HEADER ─────────────────────────────────────────────── */}
      <div className="flex-shrink-0 flex items-center justify-between px-5 py-3 bg-slate-900/95 border-b border-slate-800">
        <span className="text-white font-bold text-sm">🔵 Session</span>
        {isHandRaised && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="text-yellow-400 text-xs font-bold bg-yellow-400/10 px-3 py-1 rounded-full border border-yellow-400/20"
          >
            ✋ Hand Raised
          </motion.span>
        )}
        <span className="text-slate-500 text-xs">{uniquePeers.length} participant{uniquePeers.length !== 1 ? "s" : ""}</span>
      </div>

      {/* ── VIDEO GRID ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-hidden p-4">
        <div className={`grid gap-4 h-full ${
          anyoneSharing ? "grid-cols-1" :
          uniquePeers.length <= 1 ? "grid-cols-1" :
          uniquePeers.length <= 2 ? "grid-cols-1 md:grid-cols-2" : "grid-cols-2"
        }`}>
          {uniquePeers.map((peer) => (
            <VideoTile key={peer.id} peer={peer} />
          ))}
        </div>
      </div>

      {/* ── FLOATING REACTIONS ──────────────────────────────────── */}
      {floatingReactions.map(({ emoji, id }) => (
        <FloatingReaction key={id} emoji={emoji} id={id} onDone={() => removeFloating(id)} />
      ))}

      {/* ── CHAT PANEL ─────────────────────────────────────────── */}
      <AnimatePresence>
        {isChatOpen && (
          <motion.div
            initial={{ opacity: 0, x: 340 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 340 }}
            transition={{ type: "spring", damping: 25 }}
            className="fixed top-[52px] right-0 bottom-[100px] w-80 z-40 flex flex-col bg-slate-900 border-l border-slate-800 shadow-2xl"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <h3 className="text-white font-bold text-sm">Chat</h3>
              <button onClick={() => setIsChatOpen(false)} className="text-slate-400 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
              {chatMessages.length === 0 && (
                <p className="text-center text-slate-500 text-xs mt-8">No messages yet</p>
              )}
              {chatMessages.map((m) => (
                <div key={m.id} className={`flex flex-col ${m.senderName === localPeer?.name ? "items-end" : "items-start"}`}>
                  <span className="text-[10px] text-slate-500 mb-0.5">{m.senderName}</span>
                  <div className={`max-w-[85%] px-3 py-2 rounded-2xl text-sm ${m.senderName === localPeer?.name ? "bg-blue-600 text-white rounded-tr-sm" : "bg-slate-800 text-slate-200 rounded-tl-sm"}`}>
                    {m.message}
                  </div>
                </div>
              ))}
              <div ref={chatEndRef} />
            </div>
            <div className="p-3 border-t border-slate-800">
              <div className="flex gap-2">
                <input
                  value={chat}
                  onChange={(e) => setChat(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendMessage()}
                  placeholder="Type a message..."
                  className="flex-1 bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-white text-sm outline-none placeholder-slate-500 focus:border-blue-500/50"
                />
                <button onClick={sendMessage} className="bg-blue-600 hover:bg-blue-700 w-10 rounded-xl flex items-center justify-center transition-colors">
                  <Send className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── CONTROLS BAR ───────────────────────────────────────── */}
      <div className="flex-shrink-0 bg-slate-900/95 border-t border-slate-800 h-[100px] flex items-center justify-center gap-3 px-6 relative">

        {/* Mic */}
        <ControlBtn onClick={toggleAudio} icon={isAudioOn ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />} label={isAudioOn ? "Mute" : "Unmute"} danger={!isAudioOn} />

        {/* Camera */}
        <ControlBtn onClick={toggleVideo} icon={isVideoOn ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />} label={isVideoOn ? "Stop" : "Start"} danger={!isVideoOn} />

        {/* Screen Share */}
        <ControlBtn onClick={toggleScreenShare} icon={<ScreenShare className="w-5 h-5" />} label="Share" highlight={isScreenShared} />

        {/* Raise Hand */}
        <ControlBtn
          onClick={toggleRaiseHand}
          icon={<Hand className="w-5 h-5" />}
          label={isHandRaised ? "Lower" : "Raise Hand"}
          highlight={isHandRaised}
        />

        {/* Reactions */}
        <div className="relative">
          <ControlBtn onClick={() => setShowReactions((v) => !v)} icon={<Smile className="w-5 h-5" />} label="React" highlight={showReactions} />
          <AnimatePresence>
            {showReactions && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 10, scale: 0.9 }}
                className="absolute bottom-16 left-1/2 -translate-x-1/2 bg-slate-800 border border-slate-700 rounded-2xl p-3 shadow-2xl z-50"
              >
                <div className="grid grid-cols-4 gap-2">
                  {REACTIONS.map((emoji) => (
                    <button
                      key={emoji}
                      onClick={() => sendReaction(emoji)}
                      className="text-2xl hover:scale-125 transition-transform p-1 rounded-lg hover:bg-white/10"
                    >
                      {emoji}
                    </button>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Chat */}
        <div className="relative">
          <ControlBtn onClick={() => { setIsChatOpen((v) => !v); if (!isChatOpen) setLastReadCount(chatMessages.length); }} icon={<MessageSquare className="w-5 h-5" />} label="Chat" highlight={isChatOpen} />
          {unread > 0 && !isChatOpen && (
            <span className="absolute -top-1 -right-1 bg-red-500 text-[10px] w-5 h-5 rounded-full flex items-center justify-center font-bold text-white">
              {unread}
            </span>
          )}
        </div>

        {/* End Call */}
        <button onClick={leaveCall} className="flex flex-col items-center gap-1 group">
          <div className="w-12 h-12 bg-red-600 hover:bg-red-700 rounded-full flex items-center justify-center transition-all active:scale-95">
            <PhoneOff className="w-5 h-5 text-white" />
          </div>
          <span className="text-[10px] text-slate-400 group-hover:text-slate-200">End</span>
        </button>
      </div>
    </div>
  );
}

export default function VideoCall() {
  return (
    <HMSRoomProvider>
      <UserRoom />
    </HMSRoomProvider>
  );
}